import { Injectable } from '@angular/core';
import { LocalStorage, LocalStorageExpiry } from '@ng-idle/core';

/**
 * Idle deadline shared by every tab through localStorage (`ng2Idle.main.expiry`), so activity in one
 * tab keeps the others alive.
 *
 * The stock LocalStorageExpiry lets a single tab tear that shared deadline down for all of them:
 * `Idle.stop()` writes `last(null)` (any tab leaving the main layout: share link, review link,
 * /entry, switch-user) and `localStorage.clear()` wipes it (login, guest share-link logout,
 * NoAuthGuard cleanup). With no deadline, ng-idle computes a diff of `-timeout`, so every other tab
 * goes idle and times out within ~2 seconds — a "random" logout of a user who was only reading.
 *
 * This expiry never clears the shared deadline, and when it finds it missing it restarts the full
 * idle window from now instead of treating the session as already expired.
 */
@Injectable()
export class SharedIdleExpiry extends LocalStorageExpiry {
  private windowMs = 0;

  constructor(localStorage: LocalStorage) {
    super(localStorage);
  }

  /**
   * Idle + timeout duration, in seconds — used to rebuild a deadline that another tab wiped.
   */
  public setWindow(seconds: number): void {
    this.windowMs = Math.max(0, seconds) * 1000;
  }

  public override last(value?: Date): Date {
    if (value === null) {
      // Idle.stop() in this tab: other tabs still rely on the shared deadline, keep it.
      return super.last();
    }
    if (value !== undefined) {
      return super.last(value);
    }

    const current = super.last();
    if (current || !this.windowMs) {
      return current;
    }
    return super.last(new Date(this.now().getTime() + this.windowMs));
  }
}
